const TOKYO_TZ = "Asia/Tokyo";

function partsOf(iso: string, options: Intl.DateTimeFormatOptions): Record<string, string> {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("ja-JP", { timeZone: TOKYO_TZ, ...options }).formatToParts(d);
  const out: Record<string, string> = {};
  for (const p of parts) out[p.type] = p.value;
  return out;
}

export function formatDateTime(iso: string): string {
  const p = partsOf(iso, { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false });
  return `${p.month}月${p.day}日 ${p.hour}:${p.minute}`;
}

export function formatDate(iso: string): string {
  const p = partsOf(iso, { year: "numeric", month: "numeric", day: "numeric" });
  return `${p.year}年${p.month}月${p.day}日`;
}

export function formatShortDate(iso: string): string {
  const p = partsOf(iso, { month: "numeric", day: "numeric" });
  return `${p.month}/${p.day}`;
}

export function formatTime(iso: string): string {
  const p = partsOf(iso, { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${p.hour}:${p.minute}`;
}

export function formatWeekday(iso: string): string {
  const p = partsOf(iso, { weekday: "short" });
  return p.weekday;
}

/** Asia/Tokyo calendar date key (YYYY-MM-DD) for a UTC ISO timestamp. */
export function dateKey(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TOKYO_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const out: Record<string, string> = {};
  for (const p of parts) out[p.type] = p.value;
  return `${out.year}-${out.month}-${out.day}`;
}

/**
 * Converts a "YYYY-MM-DD" (date input) + "HH:mm" (time input) pair, both
 * interpreted as Asia/Tokyo local time, into a UTC ISO string for the API.
 * Never uses `new Date("YYYY-MM-DD")` directly — that parses as UTC midnight
 * and would silently shift the intended Tokyo date.
 */
export function tokyoLocalToIso(dateStr: string, timeStr: string): string {
  return new Date(`${dateStr}T${timeStr}:00+09:00`).toISOString();
}

/** Splits a UTC ISO timestamp into Tokyo-local date/time input values. */
export function isoToTokyoLocalParts(iso: string): { date: string; time: string } {
  const p = partsOf(iso, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return { date: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}

export function todayTokyoDateInput(): string {
  return dateKey(new Date().toISOString());
}
